import { Box } from '@iheartjane/reefer';
import type { ReservationMode } from '@iheartjane/types';
import { useEffect, useState } from 'react';

import { useCustomerDispatch } from '@jane/monolith/customer/dispatch';
import {
  analyzeSpecialsSuccess,
  removeQueryPromoCode,
  selectCart,
} from '@jane/monolith/customer/redux/cart';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { useValidatePromoCode } from '@jane/shared/data-access';
import type { Address } from '@jane/shared/types';

import { SubmittableTextInput } from './SubmittableTextInput';

interface PromoCodesProps {
  headless?: boolean;
  reservationMode: ReservationMode;
  userGroupSpecialId?: string | number;
}

export const PromoCodes = ({
  headless,
  reservationMode,
  userGroupSpecialId,
}: PromoCodesProps) => {
  const [promoCode, setPromoCode] = useState<string | undefined>();
  const cartState = useCustomerSelector(selectCart);
  const dispatch = useCustomerDispatch();

  const { data: promoCodeData, refetch } = useValidatePromoCode({
    promoCode,
    reservationMode,
    userGroupSpecialId,
    uuid: cartState.cart.uuid,
    // Coerce to resolve the mismatches in optionality between the Address implementations
    // in @jane/shared/models and @jane/shared/types
    deliveryAddress: cartState.deliveryAddress
      ? (cartState.deliveryAddress as Address)
      : undefined,
  });

  //dispatches will be updated when cart redux store is removed
  useEffect(() => {
    if (cartState?.queryPromoCode) {
      setPromoCode(cartState.queryPromoCode);
    }
  }, []);

  useEffect(() => {
    if (promoCodeData?.validated && promoCodeData?.isValid) {
      dispatch(
        analyzeSpecialsSuccess({
          cart: promoCodeData.cart,
          userGroupSpecialId,
        })
      );
    }
  }, [promoCodeData]);

  const updatePromoCode = (value?: string) => {
    if (value === promoCode) {
      refetch();
    } else {
      setPromoCode(value);
    }
    dispatch(removeQueryPromoCode());
  };

  return (
    <Box pl={headless ? 0 : 40}>
      <SubmittableTextInput
        name="promo_code"
        placeholder="Enter code here"
        submitLabel="Apply"
        onReset={() => updatePromoCode(undefined)}
        onSubmit={(value) => updatePromoCode(value)}
        validated={promoCodeData?.validated ?? false}
        hasValidationError={!promoCodeData?.isValid}
        validationMessage={
          promoCodeData?.isValid
            ? 'Success! Code will be applied if cart is eligible.'
            : 'Not Valid'
        }
      />
    </Box>
  );
};
